import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eyebrow, Card } from './UI';
import { AutoVideo } from './AutoVideo';
import { Zap, Hexagon, Plus, Minus } from 'lucide-react';
export const Materials = () => {
  const materials = [{
    tag: 'SHELL',
    name: 'GFR COMPOSITE ALLOY SHELL',
    spec: 'GLASS-FIBER REINFORCED POLYMER',
    badge: 'CRUSH RESISTANT',
    body: 'Aerospace-category composite construction. The same material class used in protective aviation helmets. Flexes on impact and returns to form. 300+ lbs of crush resistance. Not ABS. Not PVC. Not plastic. Entirely different category.'
  }, {
    tag: 'SKELETON',
    name: 'STAINLESS STEEL INNER FRAME',
    spec: 'GRADE 304 ALLOY',
    badge: 'INDUSTRIAL GRADE',
    body: 'Beneath the composite shell runs an internal Grade 304 stainless steel skeleton — providing maximum structural integrity. It distributes lateral impact force across the full frame. Zero flex failure. Zero structural compromise.'
  }, {
    tag: 'LENSES',
    name: 'POLYCARBONATE LENS SYSTEM',
    spec: 'ANTI-FOG — UV400',
    badge: 'SHATTER RESISTANT',
    body: 'High-impact polycarbonate — the same material class used in premium protective eyewear. Anti-fog nano-coating on interior. UV400 protection standard across all 6 lens colors. Tool-free swap in under 30 seconds.'
  }, {
    tag: 'LINING',
    name: 'HIGH-DENSITY NEOPRENE PADDING',
    spec: 'ANTI-BACTERIAL | MOISTURE-WICKING',
    badge: 'FIELD GRADE COMFORT',
    body: 'The face contact zone is lined with dense, premium neoprene — similar to the grade used in high-end sports padding. Anti-bacterial. Moisture-wicking. Thick enough to absorb impact without collapsing against your face during extended wear.'
  }, {
    tag: 'STRAP',
    name: 'REINFORCED NYLON STRAP',
    spec: 'DUAL-POINT ADJUST — QUICK RELEASE',
    badge: 'HEAVY-DUTY NYLON',
    body: 'The retention system uses reinforced nylon webbing — built to withstand intense outdoor use. Hundreds of hours of field use without elastic degradation. Anti-slip silicone lining. Fits 52cm–62cm head circumference.'
  }, {
    tag: 'FINISH',
    name: 'CERAMIC-INFUSED SURFACE COAT',
    spec: 'FADE PROOF | SCRATCH RESISTANT | UV STABLE',
    badge: 'PERMANENT BOND',
    body: 'The matte black finish is a ceramic-infused surface treatment bonded directly to the composite shell. Not paint. Not coating. Bonded. UV-stable. Scratch-resistant. No fading, no peeling — even after hundreds of field hours under direct sun.'
  }];
  const bars = [{
    label: 'IMPACT RESISTANCE',
    value: 96
  }, {
    label: 'LENS OPTICAL CLARITY',
    value: 98
  }, {
    label: 'MATERIAL DURABILITY',
    value: 94
  }, {
    label: 'FIELD COMFORT',
    value: 91
  }];
  const [openIndex, setOpenIndex] = useState<number>(0);
  return <section className="relative pt-[40px] pb-[40px] overflow-hidden bg-base-black" data-id="element-1108" data-mp="1cfoc9y3l54mz">
      {/* Background Video restricted to the top half */}
      <div className="absolute top-0 left-0 right-0 h-[800px] z-0" data-id="element-1109" data-mp="1h6wkwrx3clz3">
        <AutoVideo src="https://cdn.magicpatterns.com/uploads/fNp48KrVEknbEQusJkUVKW/hf_20260506_142518_1e16225a-b5f2-42aa-9e84-e3f4a3300fe1.mp4" className="w-full h-full object-cover opacity-100" data-id="element-1110" data-mp="13f9nupvbr9f9" />
        {/* Dark overlay to ensure text readability and fade seamlessly into the black background below */}
        <div className="absolute inset-0 bg-gradient-to-b from-base-black/90 via-base-black/30 to-base-black" data-id="element-1111" data-mp="204h2ddxj0i9o"></div>
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-1112" data-mp="1m7kb5bdhwys9">
        {/* Intro */}
        <div className="text-center max-w-4xl mx-auto mb-16" data-id="element-1113" data-mp="1r6f2j1pj3vnz">
          <Eyebrow data-id="element-1114" data-mp="ap9jcs4qjycx">ENGINEERING REPORT</Eyebrow>
          <h2 className="text-5xl md:text-[80px] leading-[0.9] mb-6" data-id="element-1115" data-mp="fp5ncrdfnpsh">
            THIS ISN'T PLASTIC. THIS IS{' '}
            <span className="text-acid-green" data-id="element-1116" data-mp="uyiustu5k7ad">BLACKOUT UNIT™</span> SPEC.
          </h2>
          <p className="font-barlow text-[17px] text-text-body leading-[1.9] max-w-[700px] mx-auto" data-id="element-1117" data-mp="q82gz8mf6jnh">
            "Every mask in our category is made from the same injection-molded
            ABS plastic that's been used since the 90s. It shatters. It warps.
            It fogs. It fails. We went back to zero. We asked one question: What
            would you build if there were no budget for failure?"
          </p>
        </div>

        {/* WOW Callout Box */}
        <div className="bg-acid-green-dim border border-acid-green-border border-l-4 border-l-acid-green p-8 md:p-10 max-w-[900px] mx-auto mb-20 relative" data-id="element-1118" data-mp="18zd2fa9dm47z">
          <div className="flex items-start gap-4" data-id="element-1119" data-mp="zyooefjzg3v3">
            <Zap className="text-acid-green shrink-0 mt-1" size={28} data-id="element-1120" data-mp="zzzsyhl95fn9" />
            <div data-id="element-1121" data-mp="3pnz08xgapnxv">
              <h3 className="font-bebas text-[22px] tracking-[3px] text-white mb-3" data-id="element-1122" data-mp="3poimgqczayhp">
                STRESS TEST RESULT — FIELD VERIFICATION
              </h3>
              <p className="font-barlow text-[17px] text-text-body leading-[1.8]" data-id="element-1123" data-mp="206v6bk6wekzp">
                "We placed the BLACKOUT UNIT Ultimate Mask on the ground and had
                a loaded F-250 Super Duty — 6,500 lbs — roll its front tire
                directly over the shell. The mask flexed. Absorbed the
                compression. And returned to shape. We put it back on. It still
                fit. The lenses didn't crack. That's not a stunt. That's what
                happens when you build with Glass-Fiber Reinforced composite
                instead of injection plastic."
              </p>
            </div>
          </div>
        </div>

        {/* Material Spec Sheet — Interactive Dossier */}
        <div className="max-w-[1100px] mx-auto mb-20 border border-dark-3 bg-dark-1" data-id="element-1124" data-mp="2wv1g0liog3ku">
          {/* Header row */}
          <div className="hidden md:grid grid-cols-[60px_120px_1fr_180px_60px] items-center gap-4 px-6 py-3 border-b border-dark-3 bg-base-black" data-id="element-1125" data-mp="yiayptn8f8u9">
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-1126" data-mp="25sz8yjsq6x5m">
              INDEX
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-1127" data-mp="20v62dpnig14t">
              COMPONENT
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-1128" data-mp="2ev8eswd1u2co">
              DESIGNATION
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-1129" data-mp="2os3dbf9bo2qy">
              CLASSIFICATION
            </div>
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px] text-right" data-id="element-1130" data-mp="2jts1x03dpk6m">
              EXPAND
            </div>
          </div>

          {materials.map((m, i) => {
          const isOpen = openIndex === i;
          return <div key={i} className={`border-b border-dark-3 last:border-b-0 ${isOpen ? 'bg-base-black' : ''}`} data-id="element-1131" data-mp="2tl5xwxw4g1v0" data-mpi={i}>
                <button onClick={() => setOpenIndex(isOpen ? -1 : i)} className="w-full grid grid-cols-[40px_1fr_40px] md:grid-cols-[60px_120px_1fr_180px_60px] items-center gap-4 px-6 py-5 text-left hover:bg-base-black transition-colors group" data-id="element-1132" data-mp="32kt2tto27pst">
                  <div className={`font-bebas text-[22px] leading-none ${isOpen ? 'text-acid-green' : 'text-text-dimmed group-hover:text-acid-green'} transition-colors`} data-id="element-1133" data-mp="3d3ts152aota9">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="hidden md:block font-barlow text-[11px] text-acid-green tracking-[2px]" data-id="element-1134" data-mp="386k11kz9c9kb">
                    {m.tag}
                  </div>
                  <div data-id="element-1135" data-mp="2yfp67s7y41n9">
                    <h4 className="font-bebas text-[18px] md:text-[20px] text-white leading-none" data-id="element-1136" data-mp="2yexkd945hia6">
                      {m.name}
                    </h4>
                    <div className="md:hidden font-barlow text-[10px] text-acid-green tracking-[1.5px] mt-1" data-id="element-1137" data-mp="2ki0rz38dvsr6">
                      {m.tag} — {m.spec}
                    </div>
                    <div className="hidden md:block font-barlow text-[11px] text-text-dimmed tracking-[1px] mt-1" data-id="element-1138" data-mp="34wikh9r576rp">
                      {m.spec}
                    </div>
                  </div>
                  <div className="hidden md:block" data-id="element-1139" data-mp="2ux9qwi06vu5v">
                    <span className="inline-block bg-acid-green text-black font-barlow font-bold text-[10px] tracking-[2px] px-3 py-1" data-id="element-1140" data-mp="2qxj3mo9vfw63">
                      {m.badge}
                    </span>
                  </div>
                  <div className="flex justify-end" data-id="element-1141" data-mp="2h769q5g62qna">
                    <div className={`w-8 h-8 border flex items-center justify-center transition-colors ${isOpen ? 'border-acid-green text-acid-green' : 'border-dark-3 text-text-dimmed group-hover:border-acid-green group-hover:text-acid-green'}`} data-id="element-1142" data-mp="2h5mumi68yln3">
                      {isOpen ? <Minus size={14} data-id="element-1143" data-mp="239hr749knvle" /> : <Plus size={14} data-id="element-1144" data-mp="1y8u2wqcq9suv" />}
                    </div>
                  </div>
                </button>
                <AnimatePresence initial={false} data-id="element-1145" data-mp="dr2spoywngv2">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} className="overflow-hidden" data-id="element-1146" data-mp="ipdz1ppzr98j">
                      <div className="px-6 pb-6 md:pl-[180px] md:pr-[60px] grid md:grid-cols-[1fr_auto] gap-6 items-start" data-id="element-1147" data-mp="8shx62ulllcy">
                        <p className="font-inter text-[14px] text-text-body leading-[1.8] border-l-2 border-acid-green pl-4" data-id="element-1148" data-mp="37h6roy0u6bpo">
                          {m.body}
                        </p>
                        <div className="md:hidden" data-id="element-1149" data-mp="2yk1qtwvtdec9">
                          <span className="inline-block bg-acid-green text-black font-barlow font-bold text-[10px] tracking-[2px] px-3 py-1" data-id="element-1150" data-mp="32lwv9a91k7u9">
                            {m.badge}
                          </span>
                        </div>
                        <div className="hidden md:flex items-center gap-2 text-acid-green" data-id="element-1151" data-mp="3cjuflmns5khl">
                          <Hexagon size={16} data-id="element-1152" data-mp="2exx07y4p5v9v" />
                          <span className="font-barlow text-[10px] tracking-[2px]" data-id="element-1153" data-mp="2jvouuzwq7vhr">
                            VERIFIED
                          </span>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {/* Performance Bars */}
        <div className="border border-acid-green/20 relative overflow-hidden p-8 md:p-12" data-id="element-1154" data-mp="1w404vq7dnj3z">
          {/* Background Image for this specific block */}
          <div className="absolute inset-0 z-0" data-id="element-1155" data-mp="1tkn283yydpy6">
            <img src="https://cdn.magicpatterns.com/uploads/w6GEHJzDHQ5XRpNSJscbev/hf_20260506_142335_7f850f1d-a7b9-429c-843a-31d458d2a415.png" alt="Structural Performance" className="w-full h-full object-cover opacity-70" data-id="element-1156" data-mp="1tjc008l5oq96" />
            <div className="absolute inset-0 bg-gradient-to-b from-base-black/70 via-base-black/40 to-base-black/70" data-id="element-1157" data-mp="1eo5stiabqle9"></div>
          </div>

          <div className="relative z-10" data-id="element-1158" data-mp="15wdmnjsc1yfh">
            <h3 className="font-bebas text-3xl text-center mb-10" data-id="element-1159" data-mp="15vbzwi1yfnhx">
              STRUCTURAL PERFORMANCE RATINGS
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1160" data-mp="10uwohw18gq2l">
              {bars.map((bar, i) => <div key={i} data-id="element-1161" data-mp="qyl8auyivg2v" data-mpi={i}>
                  <div className="flex justify-between font-barlow text-sm tracking-wider mb-2" data-id="element-1162" data-mp="qxrw3egsnydc">
                    <span className="text-white" data-id="element-1163" data-mp="myapuitjmx74">{bar.label}</span>
                    <span className="text-acid-green" data-id="element-1164" data-mp="cce33k7dh71r">{bar.value}/100</span>
                  </div>
                  <div className="h-2 bg-dark-3 w-full overflow-hidden" data-id="element-1165" data-mp="haz4hmyd1wrs">
                    <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: `${bar.value}%`
                }} viewport={{
                  once: true,
                  margin: '-50px'
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut',
                  delay: i * 0.2
                }} className="h-full bg-gradient-to-r from-acid-green/50 to-acid-green" data-id="element-1166" data-mp="h94j67j6g2n6" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
};